import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { useToast } from '../context/ToastContext';
import { studentLogin as loginFn } from '../api';
import { useAuth } from '../context/AuthContext';

const loginSchema = z.object({
  college_email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

const Login = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { login } = useAuth();
  
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(loginSchema)
  });

  const onSubmit = async (data) => {
    try {
      const response = await loginFn(data.college_email, data.password);
      const { access_token, student_id, role } = response;
      
      login({
        token: access_token,
        role: role || 'student',
        studentId: student_id,
        email: data.college_email
      });
      
      addToast('Welcome back!', 'success');
      navigate('/dashboard');
    } catch (err) {
      addToast(err.response?.data?.detail || 'Invalid email or password', 'error');
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <Link to="/" className="auth-logo justify-center">
          <div className="auth-logo-icon">M</div>
          <div>Million<span>Minds</span></div>
        </Link>
        
        <div className="text-center mb-8">
          <h1 className="auth-title">Welcome back</h1>
          <p className="auth-subtitle">Sign in to your student account to continue</p>
        </div>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <Input
            label="College Email"
            type="email"
            placeholder="you@college.edu"
            {...register('college_email')}
            error={errors.college_email?.message}
          />
          
          <div className="relative">
            <Input
              label="Password"
              type="password"
              placeholder="••••••••"
              {...register('password')}
              error={errors.password?.message}
            />
            <Link 
              to="/forgot-password/student" 
              className="absolute right-0 top-0 text-sm font-semi text-primary hover:underline"
            >
              Forgot password?
            </Link>
          </div>
          
          <div className="mt-8">
            <Button type="submit" variant="primary" className="w-full" size="lg" isLoading={isSubmitting}>
              Sign In
            </Button>
          </div>
        </form>
        
        <div className="auth-divider">
          <span>OR</span>
        </div>
        
        <div className="flex-col gap-3">
          <Link to="/company/login" className="btn btn-outline w-full justify-center">
            Sign in as Employer
          </Link>
        </div>
        
        <div className="auth-footer">
          Don't have an account? <Link to="/register">Create an account</Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
